import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AsyncSubject, catchError, map, Observable, of, Subject } from 'rxjs';
import { GlobalService } from 'src/app/_services/global.service';
import { environment } from 'src/environments/environment';

const devUrl = environment.backendurl;

@Injectable({
  providedIn: 'root',
})
export class HireSearchService {

  constructor(private http: HttpClient) {}

  getSummarizeCriteria(payload: any): Observable<any> {
      const url = devUrl + '/personsearch/summarize-criteria';
      return this.http.post<any>(url, payload).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('Get Person Search Listing', url)));
  }

  getPersonSearchScore(payload: any): Observable<any> {
      const url = devUrl + '/personsearch/score';
      return this.http.post<any>(url, payload).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('Get Person Search Score', url)));
  }

  candidateCompare(payload: any): Observable<any> {
      const url = devUrl + '/personsearch/candidate-compare';
      return this.http.post<any>(url, payload).pipe(map(response =>  ({ ...response, statusCode: response.status }))
      , catchError(GlobalService.handleError('Candidate Compare', url)));
    }

/**
 * Get Person Search Explanation
 * @param payload
 * @returns
 */

  getPersonSearchExplanation(payload: any): Observable<any> {
      const url = devUrl + '/personsearch/explanation';
      return this.http.post<any>(url, payload).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('Get Person Search Explanation', url)));
  }

  /**
   * Get Project Typeahead
   * @param projectname
   * @returns
   */
  getProjectTypeahead(projectname:string){
    let url = devUrl + `/hiring-project/typeahead?projectname=${encodeURIComponent(projectname)}`;
    return this.http.get(url).pipe(
      map(response=> {return response}),
      catchError(GlobalService.handleError('Failed getting projects',url))
    )
  }

  /**
   * Add a candidate to the hiring pipeline
   * @param payload - The candidate data to add
   * @returns An observable of the API response
   */
  addtoPipeline(payload: any): Observable<any> {
    const url = devUrl + '/hiring-project/addtopipeline';
    return this.http.post<any>(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Add to Pipeline', url)));
  }

  /**
   * Add a candidate to the hiring pipeline
   * @param payload - The candidate data to add
   * @returns An observable of the API response
   */
  addtoPipelineMyCandidates(payload: any): Observable<any> {
    const url = devUrl + '/hiring-project/my-candidate/addtopipeline';
    return this.http.post<any>(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Add to Pipeline', url)));
  }

  /**
   * Every candidate card on a page calls this independently on init. Rather than
   * firing one HTTP request per card (N+1 for a 20+ row page), requests made
   * within the same tick are coalesced into a single batched call. idType is kept
   * out of the batch key from the caller's side but tracked per-type here so a
   * mixed page (unlikely, but not impossible) never merges two id spaces into
   * one query - it also lets the backend search a single indexed column instead
   * of OR-ing across elastic_id/pdl_id and candidatenumber.
   * Each batch has its own result stream, so a caller only ever gets the response
   * of the batch it joined - never an older in-flight one that happens to contain
   * the same ids (which would be stale right after an add-to-pipeline).
   */
  private pendingPipelineStatusByType: Record<string, { ids: Set<string>; result$: AsyncSubject<Record<string, Record<string, boolean>>> }> = {};

  getCandidatePipelineStatus(elasticIds: string[], idType: 'elastic_id' | 'candidatenumber' = 'elastic_id'): Observable<Record<string, Record<string, boolean>>> {
    if (!elasticIds?.length) {
      return of({});
    }
    let pending = this.pendingPipelineStatusByType[idType];
    if (!pending) {
      pending = { ids: new Set<string>(), result$: new AsyncSubject() };
      this.pendingPipelineStatusByType[idType] = pending;
      setTimeout(() => this.flushPipelineStatusBatch(idType), 0);
    }
    elasticIds.forEach(id => pending.ids.add(id));
    return pending.result$.asObservable();
  }

  private flushPipelineStatusBatch(idType: string): void {
    const pending = this.pendingPipelineStatusByType[idType];
    if (!pending) return;
    delete this.pendingPipelineStatusByType[idType];
    const url = devUrl + '/hiring-project/pipeline/candidate-status';
    this.http.post<Record<string, Record<string, boolean>>>(url, { elasticid: Array.from(pending.ids), idtype: idType }).pipe(
     catchError(GlobalService.handleError('Get Candidate Pipeline Status', url))
    ).subscribe({
      next: response => {
        pending.result$.next(response || {});
        pending.result$.complete();
      },
      error: error => pending.result$.error(error)
    });
  }

  /**
   * Get a short summary of the person search results
   * @param payload - The request payload containing search parameters
   * @returns An observable of the API response
   */

  getShortSummary(payload: any): Observable<any> {
    const url = devUrl + '/personsearch/shortsummary';
    return this.http.post<any>(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Short Summary', url)));
  }

  /**
   * Get a long summary of the person search results
   * @param payload - The request payload containing search parameters
   * @returns An observable of the API response
   */
  getlongSummary(payload:any): Observable<any> {
    const url = devUrl + '/personsearch/longsummary';
    return this.http.post<any>(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Long Summary', url)));
  }


  /**
   * Fetch screening criteria suggestions
   * @param payload - The request payload containing search parameters
   * @returns An observable of the API response
   */
  fetchScreeningCriteriaSuggestions(payload: any): Observable<any> {
    const url = devUrl + '/personsearch/fetchscreeningcriteriasuggestions';
    return this.http.post<any>(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Fetch Screening Criteria Suggestions', url)));
  }

  /**
   * Get Job Fitness
   * @param payload - The request payload containing search parameters
   * @returns An observable of the API response
   */
  getJobfitness(payload: any): Observable<any> {
    const url = devUrl + '/jdfitness';
    return this.http.post<any>(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Job Fitness', url)));
  }

  /**
   * Get chip data for the ideal candidate
   * @param payload - The request payload containing search parameters
   * @returns An observable of the API response
   */
  getChipForIdealCandidate(payload: any): Observable<any> {
    const url = devUrl + '/personsearch/candidate-compare';
    return this.http.post<any>(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Chip For Ideal Candidate', url)));
  }

/**
 * Get similarity insights for a candidate
 * @param payload - The request payload containing candidate information
 * @returns An observable of the API response
 */
  getSimilarityInsights(payload: any): Observable<any> {
    const url = devUrl + '/personsearch/candidate-compare/explanation';
    return this.http.post<any>(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Get Similarity Insights', url)));
  }

  /**
   * Get the hiring notes for a candidate
   * @param payload - The request payload containing candidate information
   * @returns An observable of the API response
   */
  getHireNotes(payload: any): Observable<any> {
    const url = devUrl + '/candidate/getnotes';
    return this.http.post<any>(url, payload).pipe(
      map(response => { return response; }),
      catchError(GlobalService.handleError('Get Hire Notes', url))
    );
  }


  /**
   * Add a hiring note for a candidate
   * @param payload - The request payload containing note information
   * @returns An observable of the API response
   */
  addHireNote(payload: any): Observable<any> {
    const url = devUrl + '/candidate/addnotes';
    return this.http.post<any>(url, payload).pipe(
      map(response => { return response; }),
      catchError(GlobalService.handleError('Add Hire Note', url))
    );
  }


/**
 * Upload a resume document
 * @param data - The resume file to upload
 * @returns An observable of the API response
 */
  uploadresume(data) {
    let formData: FormData = new FormData();
    // formData.append('text', 'resume');
    formData.append('document', data);
    var url =devUrl + "/candidate/uploaddocument"
    return this.http.post(url, formData).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('Document Upload', url)));
  }

  /**
   * Save CV files
   * @param data - The CV file data to save
   * @returns An observable of the API response
   */

  savecvfiles(data) {
    var url = devUrl + "/candidate/savecvfiles"
    return this.http.post(url, data).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('savecvfiles', url)));
  }
/**
 * Fetch CV details for a candidate
 * @param candidateid - The ID of the candidate
 * @returns An observable of the API response
 */
  fetchcvdetails(candidateid){
    let url = devUrl + "/candidate/fetchcvdetails?candidatenumber="+candidateid
    return this.http.get(url).pipe(
      map(response => { return response; })
      , catchError(GlobalService.handleError('fetchcvdetails', url)))
  }

  /**
   *
   * @param projectnumber
   * @returns
   */
  getPipelineStage(projectnumber: string) {
     let url = devUrl + `/hiring-project/stage/${projectnumber}`
    return this.http.get(url).pipe(
      map(response => { return response; })
      , catchError(GlobalService.handleError('get project stages', url)))
  }

  /**
   *
   * @param payload
   * @returns
   */
  updatePipelineStage(payload: any): Observable<any> {
    const url = devUrl + '/hiring-project/pipeline/update-stage';
    return this.http.patch<any>(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Update Pipeline Stage', url)));
  }

  /**
   * Add metadata for a candidate
   * @param payload - The request payload containing metadata information
   * @returns An observable of the API response
   */
  addMetadata(payload: any): Observable<any> {
    const url = devUrl + '/personsearch/addmetadata';
    return this.http.post<any>(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Add Metadata', url)));
  }

  personSearchEnrichResults(payload: any): Observable<any> {
    const url = devUrl + '/personsearch/enrichresults';
    return this.http.post<any>(url, payload).pipe(map(response => { return response; })
    , catchError(GlobalService.handleError('Enrich Person Search Results', url)));
  }

    private chipsRefreshEvent: Subject<any> = new Subject();

    getChipsRefreshEvent(): Observable<any>{
      return this.chipsRefreshEvent.asObservable();
    }
    setChipsRefreshEvent(data: any){
      this.chipsRefreshEvent.next(data);
    }

}
